// import libraries
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { firebase as db } from '../../services/firebase';

// Utility: decide if we should fetch based on cache and flags
export const shouldFetch = ({ cachedAt, ttl, refetch, prefetch }) => {
  if (refetch) return true;
  if (!cachedAt) return true;
  if (ttl === 0) return true;
  if (prefetch) return false; // prefetch should not force fetch if we already have data
  const age = Date.now() - cachedAt;
  return age > (ttl || 0);
};

// Utility: manage Firestore subscription lifecycle per id
export const startSubscription = ({
  id,
  path, // e.g., ['users', id]
  onData,
  onError,
  getUnsub,
  setUnsub,
}) => {
  if (getUnsub(id)) return getUnsub(id); 
  const ref = doc(db, ...path);
  const unsub = onSnapshot(
    ref,
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => onError?.(err)
  );
  setUnsub(id, unsub);
  return unsub;
};

export const stopSubscription = ({ id, getUnsub, clearUnsub }) => {
  const unsub = getUnsub(id);
  if (unsub) {
    try { unsub(); } catch (_) {}
    clearUnsub(id);
  }
};

// Factory: basic keyed resource slice (by id)
export const createKeyedResourceSlice = ({
  name, // slice key, e.g., 'userRecord'
  fetchById, // async (id) => data
  subscribeById, // (id, onData, onError) => unsubscribe
  defaultTTL = 5 * 60 * 1000, // 5 minutes
}) => (set, get) => {
  const unsubscribers = new Map();

  const getUnsub = (id) => unsubscribers.get(id);
  const setUnsub = (id, fn) => unsubscribers.set(id, fn);
  const clearUnsub = (id) => unsubscribers.delete(id);

  // Internal: write to correct nesting (common[name] if present, otherwise root [name])
  const setNested = (updaterFn) => {
    set((state) => {
      const inCommon = !!(state.common && state.common[name]);
      const currentSlice = (inCommon ? state.common[name] : state[name]) || {};
      const updatedSlice = updaterFn(currentSlice, state);
      if (inCommon) {
        return {
          common: {
            ...state.common,
            [name]: updatedSlice,
          },
        };
      }
      return { [name]: updatedSlice };
    });
  };

  const setState = (updater) => setNested((curr) => ({ ...curr, ...updater }));

  const ensureState = () => {
    const state = get();
    const curr = state.common?.[name] || state[name];
    if (!curr) {
      setNested(() => ({ items: {}, isLoading: false, isPrefetching: false, error: null }));
    }
  };

  const getItems = () => {
    const state = get();
    return (state.common?.[name] || state[name])?.items || {};
  };

  const setItem = (id, updater) => {
    setNested((slice) => {
      const safeSlice = slice || { items: {} };
      const prev = (safeSlice.items && safeSlice.items[id]) || {};
      return {
        ...safeSlice,
        items: {
          ...(safeSlice.items || {}),
          [id]: { ...prev, ...updater },
        },
      };
    });
  };

  return {
    [name]: {
      items: {}, // id -> { data, cachedAt, isLoading, isPrefetching, error, hasLiveSubscription }

      // Public API used by components/hooks
      getById: async ({ id, ttl = defaultTTL, prefetch = false, refetch = false, subscribe = true } = {}) => {
        ensureState();
        const item = getItems()[id];

        // serve from cache if allowed
        if (item?.data && !shouldFetch({ cachedAt: item.cachedAt, ttl, refetch, prefetch })) {
          // ensure subscription exists if requested
          if (subscribe && !item?.hasLiveSubscription && subscribeById) {
            const unsub = subscribeById(
              id,
              (fresh) => setItem(id, { data: fresh, cachedAt: Date.now(), hasLiveSubscription: true }),
              (error) => setItem(id, { error })
            );
            setUnsub(id, unsub);
            setItem(id, { hasLiveSubscription: true });
          }
          return item.data;
        }

        // state flags
        setItem(id, prefetch ? { isPrefetching: true, error: null } : { isLoading: true, error: null });
        try {
          const data = await fetchById(id);
          setItem(id, { data, cachedAt: Date.now(), isLoading: false, isPrefetching: false, error: null });

          if (subscribe && subscribeById) {
            if (!getUnsub(id)) {
              const unsub = subscribeById(
                id,
                (fresh) => setItem(id, { data: fresh, cachedAt: Date.now(), hasLiveSubscription: true }),
                (error) => setItem(id, { error })
              );
              setUnsub(id, unsub);
            }
            setItem(id, { hasLiveSubscription: true });
          }

          return data;
        } catch (error) {
          setItem(id, { isLoading: false, isPrefetching: false, error });
          throw error;
        }
      },

      // Force refresh and optionally resubscribe
      refetchById: async ({ id, ttl = 0, subscribe = true } = {}) => {
        return get()[name].getById({ id, ttl, refetch: true, subscribe });
      },

      // Preload into cache without blocking UI
      prefetchById: async ({ id, ttl = defaultTTL, subscribe = true } = {}) => {
        return get()[name].getById({ id, ttl, prefetch: true, subscribe });
      },

      // Stop live updates for an id
      unsubscribeById: (id) => {
        const unsub = getUnsub(id);
        if (unsub) {
          try { unsub(); } catch (_) {}
          clearUnsub(id);
          setItem(id, { hasLiveSubscription: false });
        }
      },

      // Clear cache for an id
      invalidateById: (id) => setItem(id, { data: undefined, cachedAt: undefined }),

      // Clear entire slice
      reset: () => setState({ items: {} }),
    },
  };
};
